// ---------------------------------------------------------------------------
// guides.js — Smart alignment guides + rulers + draggable guides (spec D).
//
// This feature module plugs into the foundation editor-API seam. It EXCLUSIVELY
// owns the `object:moving` canvas event (translate-only snapping) and the
// `object:rotating` event (15deg rotation snap while Shift is held). It also
// adds a SECOND `after:render` painter (magenta smart-guide lines) on top of the
// foundation's viewport painter, plus an `mouse:up`/`mouse:move` listener for
// clearing snap lines and driving the ruler cursor marker.
//
// Three parts:
//   (1) Smart alignment guides — on drag, snap the moving object's edges/centers
//       to the artboard edges/center, to other objects' edges/centers, and to
//       user guides. Renders magenta lines through the fabric ctx, so they are
//       exact under any zoom/pan (they honour canvas.viewportTransform).
//   (2) Rulers — top + left DOM <canvas> strips, drawn in design (object) units,
//       re-synced to viewportTransform on every render / pan / zoom / resize.
//   (3) Draggable guides — drag out of a ruler to drop a guide; objects snap to
//       guides; a guide is dragged to reposition and dropped back on a ruler (or
//       outside the canvas) to remove it. Double-click a ruler clears all guides.
//
// Rendering surfaces:
//   - Rulers + persistent guides + drag preview  -> injected DOM inside
//     `.de-canvas-area` (needed for native pointer hit-testing on guides).
//   - Transient smart-alignment lines            -> painted on the fabric canvas
//     via after:render (canonical fabric aligning_guidelines approach).
//
// Coordinate model (matches viewport.js): objects live in SCENE/object space; the
// artboard is (0,0)-(W,H). Screen(local-to-area) px = obj * zoom + pan, where
// zoom = vpt[0] (== vpt[3]) and pan = vpt[4]/vpt[5]. viewport skew is always 0.
// ---------------------------------------------------------------------------

import './guides.css';

const RULER_SIZE = 22;      // px thickness of each ruler
const SNAP_PX = 6;          // snap threshold, in SCREEN px (constant feel at any zoom)
const GUIDE_HIT = 9;        // px grab-strip width around a guide line
const MAJOR_TICK = 8;       // px length of a labelled tick
const MINOR_TICK = 4;       // px length of an unlabelled tick
const TARGET_TICK_PX = 80;  // desired screen px between labelled ticks
const SUBDIV = 5;           // minor ticks between labelled ticks

const SMART_COLOR = '#e91e8c';   // magenta smart-alignment lines
const RULER_BG = '#232323';
const RULER_TICK = '#6f6f6f';
const RULER_TEXT = '#9a9a9a';
const RULER_MARK = '#e0e0e0';
const RULER_ARTBOARD = 'rgba(120,150,220,0.16)';

export function registerGuidesAndRulers(editor) {
  const canvas = editor.canvas;

  // The foundation renders the fabric canvas inside `.de-canvas-area`. Derive it
  // from fabric's own wrapper so this module does not depend on the editor seam
  // exposing `canvasAreaRef` (which it currently does not — see report).
  const areaEl = (canvas.wrapperEl && canvas.wrapperEl.parentNode)
    || (editor.canvasAreaRef && editor.canvasAreaRef.current);
  if (!areaEl) return; // nothing to attach to; bail quietly

  const state = {
    visible: true,          // rulers + guides shown
    guides: [],             // [{ id, axis:'v'|'h', pos:number(obj units), _el }]
    smartLines: [],         // [{ axis, pos, from, to }] transient, object coords
    pointer: null,          // { x, y } object coords for the ruler marker
    drag: null,             // active create/move drag descriptor
    lastSig: '',            // viewportTransform + size signature (skip idle syncs)
    idSeq: 1,
    rafId: null,
  };

  // =========================================================================
  //  DOM overlay construction
  // =========================================================================
  const rulerH = document.createElement('canvas');
  rulerH.className = 'de-ruler de-ruler-h';
  const rulerV = document.createElement('canvas');
  rulerV.className = 'de-ruler de-ruler-v';
  const corner = document.createElement('div');
  corner.className = 'de-ruler-corner';
  corner.style.width = RULER_SIZE + 'px';
  corner.style.height = RULER_SIZE + 'px';
  const guideLayer = document.createElement('div');
  guideLayer.className = 'de-guide-layer';

  areaEl.appendChild(guideLayer);
  areaEl.appendChild(rulerH);
  areaEl.appendChild(rulerV);
  areaEl.appendChild(corner);

  // Keep wheel-zoom working when the pointer is over a ruler / guide strip: those
  // elements capture pointer events (so fabric never gets the guide grab), which
  // would otherwise swallow the wheel. Forward it to the viewport zoom.
  const forwardWheel = (e) => {
    e.preventDefault();
    const vp = editor.viewport;
    if (!vp) return;
    const factor = e.deltaY > 0 ? 0.92 : 1.08;
    vp.zoomTo(vp.getZoom() * factor, { x: e.clientX, y: e.clientY });
  };
  [rulerH, rulerV, corner, guideLayer].forEach((el) => el.addEventListener('wheel', forwardWheel, { passive: false }));

  // =========================================================================
  //  Coordinate helpers (self-contained; pure zoom/pan viewportTransform)
  // =========================================================================
  const vpt = () => canvas.viewportTransform;
  const zoom = () => vpt()[0] || 1;
  const areaRect = () => areaEl.getBoundingClientRect();
  // client px -> object (scene) coords
  const clientToObjX = (cx) => (cx - areaRect().left - vpt()[4]) / zoom();
  const clientToObjY = (cy) => (cy - areaRect().top - vpt()[5]) / zoom();
  // object coords -> area-relative (local) px
  const objToLocalX = (ox) => ox * vpt()[0] + vpt()[4];
  const objToLocalY = (oy) => oy * vpt()[3] + vpt()[5];

  // =========================================================================
  //  Ruler drawing
  // =========================================================================
  function niceStep(z) {
    const raw = TARGET_TICK_PX / z;
    if (!isFinite(raw) || raw <= 0) return 1;
    const pow = Math.pow(10, Math.floor(Math.log10(raw)));
    const norm = raw / pow;
    let lead;
    if (norm < 1.5) lead = 1;
    else if (norm < 3) lead = 2;
    else if (norm < 7) lead = 5;
    else lead = 10;
    return lead * pow;
  }

  function drawRuler(axis) {
    const cvs = axis === 'h' ? rulerH : rulerV;
    const aW = areaEl.clientWidth;
    const aH = areaEl.clientHeight;
    const len = Math.max(0, (axis === 'h' ? aW : aH) - RULER_SIZE);
    const thick = RULER_SIZE;
    const cssW = axis === 'h' ? len : thick;
    const cssH = axis === 'h' ? thick : len;
    const dpr = window.devicePixelRatio || 1;

    // position + backing store
    cvs.style.left = axis === 'h' ? RULER_SIZE + 'px' : '0px';
    cvs.style.top = axis === 'h' ? '0px' : RULER_SIZE + 'px';
    cvs.style.width = cssW + 'px';
    cvs.style.height = cssH + 'px';
    const bw = Math.max(1, Math.round(cssW * dpr));
    const bh = Math.max(1, Math.round(cssH * dpr));
    if (cvs.width !== bw) cvs.width = bw;
    if (cvs.height !== bh) cvs.height = bh;
    if (!cssW || !cssH) return;

    const ctx = cvs.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, cssW, cssH);
    ctx.fillStyle = RULER_BG;
    ctx.fillRect(0, 0, cssW, cssH);

    const z = zoom();
    const pan = axis === 'h' ? vpt()[4] : vpt()[5];
    // ruler-canvas coord s -> object; object -> ruler-canvas coord s
    const objAt = (s) => (s + RULER_SIZE - pan) / z;
    const sAt = (o) => o * z + pan - RULER_SIZE;

    // artboard extent highlight (0 .. artboard length on this axis)
    const ab = editor.getArtboard();
    const abLen = axis === 'h' ? ab.width : ab.height;
    const sa = sAt(0);
    const sb = sAt(abLen);
    ctx.fillStyle = RULER_ARTBOARD;
    if (axis === 'h') ctx.fillRect(Math.min(sa, sb), 0, Math.abs(sb - sa), thick);
    else ctx.fillRect(0, Math.min(sa, sb), thick, Math.abs(sb - sa));

    const step = niceStep(z);
    if (!(step > 0)) return;
    const minorStep = step / SUBDIV;
    const decimals = step < 1 ? 1 : 0;

    // Index ticks by an integer counter (k * minorStep) so float accumulation
    // never mis-classifies a labelled tick or drifts the labels.
    const startIdx = Math.floor(objAt(0) / minorStep);
    const endIdx = Math.ceil(objAt(len) / minorStep);
    ctx.font = '9px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
    ctx.textBaseline = 'top';
    ctx.lineWidth = 1;

    let guard = 0;
    for (let k = startIdx; k <= endIdx && guard < 5000; k += 1, guard += 1) {
      const o = k * minorStep;
      const s = Math.round(sAt(o)) + 0.5;
      const isMajor = (k % SUBDIV === 0); // major == exact multiple of `step`
      const tickLen = isMajor ? MAJOR_TICK : MINOR_TICK;
      ctx.strokeStyle = RULER_TICK;
      ctx.beginPath();
      if (axis === 'h') {
        ctx.moveTo(s, thick);
        ctx.lineTo(s, thick - tickLen);
      } else {
        ctx.moveTo(thick, s);
        ctx.lineTo(thick - tickLen, s);
      }
      ctx.stroke();
      if (isMajor) {
        const label = o.toFixed(decimals);
        ctx.fillStyle = RULER_TEXT;
        if (axis === 'h') ctx.fillText(label, Math.round(sAt(o)) + 2, 2);
        else ctx.fillText(label, 2, Math.round(sAt(o)) + 2);
      }
    }

    // live pointer marker
    if (state.pointer) {
      const pv = axis === 'h' ? state.pointer.x : state.pointer.y;
      const s = Math.round(sAt(pv)) + 0.5;
      ctx.strokeStyle = RULER_MARK;
      ctx.beginPath();
      if (axis === 'h') { ctx.moveTo(s, 0); ctx.lineTo(s, thick); }
      else { ctx.moveTo(0, s); ctx.lineTo(thick, s); }
      ctx.stroke();
    }
  }

  // =========================================================================
  //  Guide DOM positioning
  // =========================================================================
  function positionOneGuide(g) {
    const el = g._el;
    if (!el) return;
    const aW = areaEl.clientWidth;
    const aH = areaEl.clientHeight;
    if (g.axis === 'v') {
      const x = objToLocalX(g.pos);
      if (x < RULER_SIZE - 1 || x > aW) { el.style.display = 'none'; return; }
      el.style.display = 'block';
      el.style.left = (x - Math.floor(GUIDE_HIT / 2)) + 'px';
      el.style.top = RULER_SIZE + 'px';
      el.style.width = GUIDE_HIT + 'px';
      el.style.height = Math.max(0, aH - RULER_SIZE) + 'px';
    } else {
      const y = objToLocalY(g.pos);
      if (y < RULER_SIZE - 1 || y > aH) { el.style.display = 'none'; return; }
      el.style.display = 'block';
      el.style.top = (y - Math.floor(GUIDE_HIT / 2)) + 'px';
      el.style.left = RULER_SIZE + 'px';
      el.style.height = GUIDE_HIT + 'px';
      el.style.width = Math.max(0, aW - RULER_SIZE) + 'px';
    }
  }

  function positionGuides() {
    state.guides.forEach(positionOneGuide);
  }

  function makeGuideEl(g) {
    const el = document.createElement('div');
    el.className = 'de-guide de-guide-' + g.axis;
    const line = document.createElement('i');
    line.className = 'de-guide-line';
    if (g.axis === 'v') line.style.left = Math.floor(GUIDE_HIT / 2) + 'px';
    else line.style.top = Math.floor(GUIDE_HIT / 2) + 'px';
    el.appendChild(line);
    el.addEventListener('pointerdown', (e) => onGuidePointerDown(e, g));
    guideLayer.appendChild(el);
    g._el = el;
    positionOneGuide(g);
    return el;
  }

  function addGuide(axis, pos) {
    const g = { id: state.idSeq++, axis, pos };
    state.guides.push(g);
    makeGuideEl(g);
    return g;
  }

  function removeGuide(g) {
    const i = state.guides.indexOf(g);
    if (i >= 0) state.guides.splice(i, 1);
    if (g._el && g._el.parentNode) g._el.parentNode.removeChild(g._el);
    g._el = null;
  }

  function clearGuides() {
    state.guides.slice().forEach(removeGuide);
  }

  // =========================================================================
  //  Guide drag / create interaction
  // =========================================================================
  function startPointerDrag() {
    window.addEventListener('pointermove', onDragMove, true);
    window.addEventListener('pointerup', onDragEnd, true);
    window.addEventListener('pointercancel', onDragEnd, true);
  }
  function endPointerDrag() {
    window.removeEventListener('pointermove', onDragMove, true);
    window.removeEventListener('pointerup', onDragEnd, true);
    window.removeEventListener('pointercancel', onDragEnd, true);
  }

  // Drag out of a ruler -> create a new guide of the perpendicular axis.
  function onRulerPointerDown(e, axis) {
    if (e.button !== 0) return;
    if (editor.getMode && editor.getMode()) return; // don't add guides mid crop/erase
    e.preventDefault();
    const pos = axis === 'h' ? clientToObjY(e.clientY) : clientToObjX(e.clientX);
    const g = addGuide(axis, pos);
    state.drag = { guide: g, mode: 'create' };
    startPointerDrag();
  }

  // Press on an existing guide strip -> reposition it.
  function onGuidePointerDown(e, g) {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    state.drag = { guide: g, mode: 'move' };
    startPointerDrag();
  }

  function onDragMove(e) {
    const d = state.drag;
    if (!d) return;
    if (!areaEl.isConnected) { endPointerDrag(); state.drag = null; return; }
    d.guide.pos = d.guide.axis === 'h' ? clientToObjY(e.clientY) : clientToObjX(e.clientX);
    positionOneGuide(d.guide);
  }

  function onDragEnd(e) {
    const d = state.drag;
    state.drag = null;
    endPointerDrag();
    if (!d) return;
    const r = areaRect();
    const inside = e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
    // Dropped back on the matching ruler band, or outside the area -> remove.
    const onRuler = d.guide.axis === 'h'
      ? (e.clientY - r.top) < RULER_SIZE
      : (e.clientX - r.left) < RULER_SIZE;
    if (!inside || onRuler) removeGuide(d.guide);
    else positionOneGuide(d.guide);
  }

  rulerH.addEventListener('pointerdown', (e) => onRulerPointerDown(e, 'h'));
  rulerV.addEventListener('pointerdown', (e) => onRulerPointerDown(e, 'v'));
  rulerH.addEventListener('dblclick', clearGuides);
  rulerV.addEventListener('dblclick', clearGuides);
  corner.addEventListener('dblclick', clearGuides);

  // =========================================================================
  //  Smart alignment snapping (owns object:moving)
  // =========================================================================
  function buildRefs(target) {
    const ab = editor.getArtboard();
    const W = ab.width;
    const H = ab.height;
    const vx = []; // vertical reference lines {pos, from, to}
    const hy = []; // horizontal reference lines {pos, from, to}
    // artboard edges + centers
    vx.push({ pos: 0, from: 0, to: H });
    vx.push({ pos: W / 2, from: 0, to: H });
    vx.push({ pos: W, from: 0, to: H });
    hy.push({ pos: 0, from: 0, to: W });
    hy.push({ pos: H / 2, from: 0, to: W });
    hy.push({ pos: H, from: 0, to: W });
    // user guides (only when shown)
    if (state.visible) {
      state.guides.forEach((g) => {
        if (g.axis === 'v') vx.push({ pos: g.pos, from: 0, to: H });
        else hy.push({ pos: g.pos, from: 0, to: W });
      });
    }
    // other objects (skipped for a multi-object selection: members carry
    // group-relative coords that would poison absolute snapping)
    if (target.type !== 'activeSelection') {
      canvas.getObjects().forEach((o) => {
        if (o === target || o.visible === false) return;
        const r = o.getBoundingRect(true, true);
        const l = r.left;
        const t = r.top;
        const rr = r.left + r.width;
        const bb = r.top + r.height;
        vx.push({ pos: l, from: t, to: bb });
        vx.push({ pos: l + r.width / 2, from: t, to: bb });
        vx.push({ pos: rr, from: t, to: bb });
        hy.push({ pos: t, from: l, to: rr });
        hy.push({ pos: t + r.height / 2, from: l, to: rr });
        hy.push({ pos: bb, from: l, to: rr });
      });
    }
    return { vx, hy };
  }

  // best alignment of any target edge to any reference within threshold
  function bestSnap(edges, refs, thr) {
    let best = null;
    for (let i = 0; i < edges.length; i += 1) {
      for (let j = 0; j < refs.length; j += 1) {
        const delta = refs[j].pos - edges[i];
        if (Math.abs(delta) <= thr && (!best || Math.abs(delta) < Math.abs(best.delta))) {
          best = { delta, ref: refs[j] };
        }
      }
    }
    return best;
  }

  function onObjectMoving(opt) {
    if (editor.getMode && editor.getMode()) return; // let crop/erase modes move freely
    const target = opt.target;
    if (!target) { state.smartLines = []; return; }
    target.setCoords();
    const thr = SNAP_PX / zoom();
    const b = target.getBoundingRect(true, true);
    const refs = buildRefs(target);
    const bx = bestSnap([b.left, b.left + b.width / 2, b.left + b.width], refs.vx, thr);
    const by = bestSnap([b.top, b.top + b.height / 2, b.top + b.height], refs.hy, thr);
    if (bx) target.set('left', target.left + bx.delta);
    if (by) target.set('top', target.top + by.delta);
    if (bx || by) target.setCoords();

    const lines = [];
    const b2 = target.getBoundingRect(true, true);
    if (bx) lines.push({ axis: 'v', pos: bx.ref.pos, from: Math.min(b2.top, bx.ref.from), to: Math.max(b2.top + b2.height, bx.ref.to) });
    if (by) lines.push({ axis: 'h', pos: by.ref.pos, from: Math.min(b2.left, by.ref.from), to: Math.max(b2.left + b2.width, by.ref.to) });
    state.smartLines = lines;
  }

  function clearSmart() {
    if (state.smartLines.length) {
      state.smartLines = [];
      canvas.requestRenderAll();
    }
  }

  // =========================================================================
  //  Rotation snap (owns object:rotating) — 15deg increments while Shift held
  // =========================================================================
  function onObjectRotating(opt) {
    if (editor.getMode && editor.getMode()) return;
    const t = opt.target;
    if (!t) return;
    const e = opt.e;
    if (e && e.shiftKey) {
      let a = t.angle % 360;
      if (a < 0) a += 360;
      t.set('angle', (Math.round(a / 15) * 15) % 360);
    }
  }

  // =========================================================================
  //  Smart-line painter (second after:render, drawn on top of the artboard)
  // =========================================================================
  function drawSmartLines(ctx) {
    if (!ctx || !state.smartLines.length || editor.isExporting()) return;
    const t = vpt();
    ctx.save();
    ctx.transform(t[0], t[1], t[2], t[3], t[4], t[5]);
    ctx.lineWidth = 1 / (t[0] || 1);
    ctx.strokeStyle = SMART_COLOR;
    state.smartLines.forEach((l) => {
      ctx.beginPath();
      if (l.axis === 'v') { ctx.moveTo(l.pos, l.from); ctx.lineTo(l.pos, l.to); }
      else { ctx.moveTo(l.from, l.pos); ctx.lineTo(l.to, l.pos); }
      ctx.stroke();
    });
    ctx.restore();
  }

  // =========================================================================
  //  Sync scheduling
  // =========================================================================
  function sig() {
    const t = vpt();
    return t.join(',') + '|' + areaEl.clientWidth + 'x' + areaEl.clientHeight;
  }
  function requestSync() {
    if (state.rafId != null) return;
    state.rafId = window.requestAnimationFrame(() => {
      state.rafId = null;
      if (!areaEl.isConnected) return;
      drawRuler('h');
      drawRuler('v');
      positionGuides();
    });
  }

  function onAfterRender(opt) {
    drawSmartLines(opt && opt.ctx);
    const s = sig();
    if (s !== state.lastSig) { state.lastSig = s; requestSync(); }
  }

  function onAreaPointerMove(e) {
    state.pointer = { x: clientToObjX(e.clientX), y: clientToObjY(e.clientY) };
    requestSync();
  }
  function onAreaPointerLeave() {
    if (state.pointer) { state.pointer = null; requestSync(); }
  }
  areaEl.addEventListener('pointermove', onAreaPointerMove);
  areaEl.addEventListener('pointerleave', onAreaPointerLeave);

  // =========================================================================
  //  Visibility toggle
  // =========================================================================
  function setVisible(v) {
    state.visible = v;
    const disp = v ? '' : 'none';
    rulerH.style.display = disp;
    rulerV.style.display = disp;
    corner.style.display = disp;
    guideLayer.style.display = disp;
    if (v) { drawRuler('h'); drawRuler('v'); positionGuides(); }
    // Force the toolbar to re-evaluate the toggle's active state (no dedicated
    // React re-render seam; refreshLayers just setState({layers}) -> re-render).
    if (editor.refreshLayers) editor.refreshLayers();
  }

  // =========================================================================
  //  Wire into the foundation seam
  // =========================================================================
  canvas.on('object:moving', onObjectMoving);
  canvas.on('object:rotating', onObjectRotating);
  canvas.on('after:render', onAfterRender);
  canvas.on('mouse:up', clearSmart);

  editor.addToolButton({
    id: 'guides-rulers-toggle',
    title: 'Rigle si ghidaje (arata / ascunde)',
    icon: 'Rigle',
    onClick: () => setVisible(!state.visible),
    isActive: () => state.visible,
  });

  // initial paint
  requestSync();
}
